import { Injectable, NgZone, inject } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import turfArea from '@turf/area';
import { ICoordenadas } from 'modelos/src';
import { MAPBOX_TOKEN } from 'src/environments/environment';
import { coordenadasToGeoJSONPolygon, latLngToLngLat } from './mapbox-coords';

/** Estilos base disponibles (equivalen al selector de tipo de mapa de Google). */
export type TipoMapaBase = 'calle' | 'satelite';

const ESTILOS: Record<TipoMapaBase, string> = {
  calle: 'mapbox://styles/mapbox/standard',
  satelite: 'mapbox://styles/mapbox/standard-satellite',
};

/** Centro por defecto: AMBA (Buenos Aires). */
const CENTRO_DEFAULT: [number, number] = [-58.381592, -34.603722];

export interface CrearMapaOpts {
  center?: ICoordenadas;
  zoom?: number;
  estilo?: TipoMapaBase;
  /** Si false, no agrega los controles de navegación. */
  navControls?: boolean;
}

/**
 * Helpers compartidos de Mapbox para `seguridad-admin-web`.
 *
 * No asume un único mapa global (admin abre varios mapas, incluso en
 * diálogos), por eso `crearMapa` devuelve instancias independientes.
 */
@Injectable({ providedIn: 'root' })
export class MapboxService {
  private ngZone = inject(NgZone);
  private tokenInicializado = false;

  estiloUrl(tipo: TipoMapaBase): string {
    return ESTILOS[tipo] ?? ESTILOS['calle'];
  }

  /** Setea el access token una sola vez por sesión. */
  initToken(): void {
    if (!this.tokenInicializado) {
      mapboxgl.accessToken = MAPBOX_TOKEN;
      this.tokenInicializado = true;
    }
  }

  /**
   * Crea una instancia de mapa fuera de la zona de Angular (evita el overhead
   * de change detection en el loop de render de Mapbox, patrón de gestion).
   */
  crearMapa(
    container: string | HTMLElement,
    opts: CrearMapaOpts = {},
  ): mapboxgl.Map {
    this.initToken();
    const center = opts.center
      ? latLngToLngLat(opts.center)
      : CENTRO_DEFAULT;

    let map!: mapboxgl.Map;
    this.ngZone.runOutsideAngular(() => {
      map = new mapboxgl.Map({
        container,
        style: this.estiloUrl(opts.estilo ?? 'calle'),
        center,
        zoom: opts.zoom ?? 10,
        maxZoom: 19,
        attributionControl: false,
      });
      // Sin controles de navegación: nadie los usa y tapan los paneles.
      // El zoom funciona con rueda/pinch. Mantener `navControls: true` si
      // alguna vista los necesitara explícitamente en el futuro.
      if (opts.navControls === true) {
        map.addControl(
          new mapboxgl.NavigationControl({ showCompass: false }),
          'bottom-right',
        );
      }
    });
    return map;
  }

  /** Área en hectáreas de uno o más anillos (reemplaza geometry de Google). */
  calcularAreaHa(rings: ICoordenadas[]): number {
    if (!rings || rings.length < 3) {
      return 0;
    }
    const feature = coordenadasToGeoJSONPolygon([rings]);
    const m2 = turfArea(feature);
    return +(m2 / 10000).toFixed(2);
  }
}
